// Multi-cell selection controls for the crew grid (#589), shown in edit mode
// while cells are selected — plus the report of what the last bulk action did.
//
// The bar shows what an action WILL touch before it runs: how many cells,
// how many weekend cells the Skip Sat/Sun rule leaves out, and every selected
// cell that will be refused and why. The report then says what DID happen.
// Between the two, a refused cell is never a surprise.

import type { RefObject } from 'react';
import { parseCrewValue } from './lib/crewClipboard';

const css = `
  .mp-sel { position: relative; display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 6px 12px; background: var(--ew-quality-50); border: 1px solid var(--ew-quality-100); border-left: 3px solid var(--ew-quality); border-radius: var(--ew-r-md); font: var(--ew-fw-regular) 12px var(--ew-font); color: var(--ew-dark); }
  .mp-sel-count { font-weight: 700; font-variant-numeric: tabular-nums; }
  .mp-sel-muted { color: var(--ew-n-500); }
  .mp-sel-set { display: inline-flex; align-items: center; gap: 6px; }
  .mp-sel-set input { width: 64px; padding: 4px 6px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-sm); font: var(--ew-fw-semibold) 13px var(--ew-font); text-align: center; }
  .mp-sel-set input[aria-invalid="true"] { border-color: var(--ew-danger); }
  .mp-sel-err { color: var(--ew-danger); font-weight: 600; }
  .mp-sel-wk { display: inline-flex; align-items: center; gap: 5px; cursor: pointer; }
  .mp-sel-info { flex: 1 1 340px; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .mp-sel-refused { display: inline; margin-left: 8px; }
  .mp-sel-refused summary { display: inline; cursor: pointer; padding: 1px 8px; border-radius: var(--ew-r-pill); background: var(--ew-danger-50); color: var(--ew-danger-700); font-weight: 600; }
  .mp-sel-refused ul { position: absolute; z-index: 40; left: 14px; right: 14px; top: calc(100% + 4px); margin: 0; padding: 8px 10px 8px 28px; white-space: normal; background: var(--ew-white); border: 1px solid var(--ew-danger-100); border-radius: var(--ew-r-md); box-shadow: var(--ew-shadow-2); color: var(--ew-danger-700); }
  .mp-sel-short { display: none; }
  /* Narrow bar (#589 follow-up): the bar rides in the sticky edit tray, so
     every line it wraps to is a line of grid hidden for the whole session.
     A CONTAINER query on its slot in the tray (AssignmentTray), not a media
     query: what wraps is the bar's own width, whatever the page around it.
     Same controls, shorter words: short labels swap in, the paste hint and
     the "Set to" label go (the box says "Set"), and padding tightens. */
  @container (max-width: 1120px) {
    .mp-sel { gap: 6px; padding: 4px 8px; }
    .mp-sel-long { display: none; }
    .mp-sel-short { display: inline; }
    .mp-sel-info { flex: 1 1 120px; }
    .mp-sel-set input { width: 48px; padding: 3px 4px; }
    .mp-sel .btn { padding: 3px 8px !important; }
  }
  .mp-bulk ul { margin: 4px 0 0; padding-left: 18px; }
  .mp-bulk { padding: 8px 14px; margin-bottom: 10px; background: var(--ew-white); border: 1px solid var(--ew-n-200); border-left: 3px solid var(--ew-performance); border-radius: var(--ew-r-md); font: var(--ew-fw-regular) 12px/1.5 var(--ew-font); color: var(--ew-dark); position: relative; }
  .mp-bulk-x { position: absolute; top: 4px; right: 6px; border: none; background: transparent; font-size: 16px; color: var(--ew-n-500); cursor: pointer; }
`;

export interface BulkReport {
  /** What happened, one sentence. */
  title: string;
  /** Side notes: weekends left out, clipped rows/columns, replaced crew. */
  notes: string[];
  /** Every refused cell, grouped (describeRefusals). Empty = nothing refused. */
  refused: string[];
  /** Nothing was staged (all refused, all no-ops, or invalid input). */
  failed?: boolean;
}

export function BulkReportPanel({ report, onDismiss }: { report: BulkReport; onDismiss: () => void }) {
  return (
    <div className="mp-bulk mp-noprint" role="status" aria-live="polite"
      style={report.failed ? { borderLeftColor: 'var(--ew-danger)' } : undefined}>
      <style>{css}</style>
      <button type="button" className="mp-bulk-x" aria-label="Dismiss" onClick={onDismiss}>×</button>
      <div style={{ fontWeight: 700, paddingRight: 20 }}>{report.title}</div>
      {report.notes.map((n, i) => <div key={i} className="mp-sel-muted">{n}</div>)}
      {report.refused.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <b style={{ color: 'var(--ew-danger-700)' }}>Not changed:</b>
          <ul>{report.refused.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/** What the selection covers; null while nothing is selected. */
export interface SelectionSummary {
  /** Size of the rectangle: crew lines × days (after the weekend rule). */
  rows: number;
  days: number;
  /** Cells an action would write. */
  writable: number;
  /** Selected cells that will be refused, and the same grouped and worded. */
  refusedCount: number;
  refused: string[];
  weekendsSkipped: number;
}

/**
 * ⚠️ Rendered for the whole of edit mode, not only while something is
 * selected, and kept to one line. It sits ABOVE the grid, so a bar that
 * appeared — or grew a refusal list — the moment a drag reached its second
 * cell pushed the grid down under the pointer mid-drag, and the drag ended on
 * the wrong row. Idle, it carries the hint for how to select instead.
 */
export default function SelectionBar({
  info, skipWeekends, onSkipWeekends,
  value, onValue, inputRef, busy, onSet, onClear, onCopy, onDeselect,
}: {
  info: SelectionSummary | null;
  skipWeekends: boolean;
  onSkipWeekends: (on: boolean) => void;
  value: string;
  onValue: (v: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  busy: boolean;
  onSet: (n: number) => void;
  onClear: () => void;
  onCopy: () => void;
  onDeselect: () => void;
}) {
  const writable = info?.writable ?? 0;
  const parsed = value.trim() === '' ? null : parseCrewValue(value);
  const canSet = parsed?.ok === true && writable > 0 && !busy;
  // Not gated on `busy`: the page refuses a set while a save is in flight
  // AND says so. Swallowing Enter here left the scheduler thinking it landed.
  const submit = () => { if (parsed?.ok && writable > 0) onSet(parsed.value); };

  return (
    <div className="mp-sel mp-noprint" role="group" aria-label="Selected cells">
      <style>{css}</style>
      {info ? (
        <span className="mp-sel-info">
          <span className="mp-sel-count">{writable}</span> cell{writable === 1 ? '' : 's'}<span className="mp-sel-long"> selected</span>
          <span className="mp-sel-muted mp-sel-long"> · {info.rows} line{info.rows === 1 ? '' : 's'} × {info.days} day{info.days === 1 ? '' : 's'}</span>
          <span className="mp-sel-muted mp-sel-short"> · {info.rows}×{info.days}</span>
          {/* One line, list in a popover: nothing here may resize the bar
              mid-drag. The cells are also hatched on the grid, and Set /
              Clear / Paste list them again afterwards. Placed BEFORE the
              weekend note: this span ellipsizes from the end, and on a
              narrow bar the refusal warning is the part that must survive. */}
          {info.refusedCount > 0 && (
            <details className="mp-sel-refused">
              <summary>{info.refusedCount} won’t change</summary>
              <ul>{info.refused.map((l, i) => <li key={i}>{l}</li>)}</ul>
            </details>
          )}
          {/* In here, not beside the checkbox: this appears the moment a drag
              crosses a weekend, and only this span can grow without wrapping
              the bar mid-drag (it ellipsizes instead). */}
          {skipWeekends && info.weekendsSkipped > 0 && (
            <span className="mp-sel-muted" title="Saturday and Sunday cells inside the rectangle that Skip Sat/Sun leaves out">
              {' · '}{info.weekendsSkipped} Sat/Sun<span className="mp-sel-long"> left out</span>
            </span>
          )}
        </span>
      ) : (
        <span className="mp-sel-muted mp-sel-info">
          <span className="mp-sel-long"><b style={{ color: 'var(--ew-dark)' }}>Select several cells:</b> drag across them, or click one and shift-click another.</span>
          <span className="mp-sel-short">Drag or shift-click to select</span>
        </span>
      )}

      <span className="mp-sel-set">
        <label htmlFor="mp-sel-value" className="mp-sel-muted mp-sel-long">Set to</label>
        <input
          id="mp-sel-value" ref={inputRef} inputMode="numeric" autoComplete="off"
          placeholder="Set" aria-label="Set the selected cells to"
          title={parsed?.ok === false ? parsed.why : undefined}
          data-grid-paste="true"
          value={value} aria-invalid={parsed?.ok === false}
          onChange={(e) => onValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            if (e.key === 'Escape') { e.preventDefault(); onDeselect(); }
          }}
        />
        <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} disabled={!canSet} onClick={submit}>
          Apply
        </button>
        {parsed?.ok === false && info && <span className="mp-sel-err mp-sel-long">{parsed.why}</span>}
      </span>

      <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}
        disabled={writable === 0 || busy} onClick={onClear} title="Clear the selected cells (Delete)">
        Clear
      </button>
      <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}
        disabled={!info} onClick={onCopy} title="Copy as tab-separated values — pastes into Excel (Ctrl+C)">
        Copy
      </button>
      <span className="mp-sel-muted mp-sel-long" title="Paste a block copied from this grid or from Excel; it lands from the top-left selected cell. You can also click a single cell and paste.">
        Ctrl+V pastes
      </span>

      <label className="mp-sel-wk" title="Most crews run Mon–Fri. On: Saturday and Sunday are left out of set, clear, copy and paste. Off: every day counts.">
        <input type="checkbox" checked={skipWeekends} onChange={(e) => onSkipWeekends(e.target.checked)} />
        <span className="mp-sel-long">Skip Sat/Sun</span><span className="mp-sel-short">No Sat/Sun</span>
      </label>

      <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px', marginLeft: 'auto' }}
        disabled={!info} onClick={onDeselect} title="Deselect (Esc)" aria-label="Deselect">
        <span className="mp-sel-long">Deselect</span><span className="mp-sel-short">✕</span>
      </button>
    </div>
  );
}
